// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import seoFieldDefinitions from "@conklin/App/Widgets/SearchEngineOptimization/Fields";
import { SeoAppProps, SeoHandlerProps } from "@conklin/App/Widgets/SearchEngineOptimization/Type";
import { WithContext } from "@conklin/App/Widgets/SearchEngineOptimization/Type/schema";
import safeJsonLdReplacer from "@conklin/App/Widgets/SearchEngineOptimization/Utilities/safeJsonLdReplacer";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

// Extensions
import Brand from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaBrand";
import BreadcrumbList from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaBreadcrumbList";
import CollectionPage from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaCollectionPage";
import ContactPage from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaContactPage";
import LocalBusiness from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaLocalBusiness";
import Organization from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaOrganization";
import Product from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaProduct";
import SiteNavigationElement from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaSiteNavigationElement";
import WebPage from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaWebPage";
import Website from "@conklin/App/Widgets/SearchEngineOptimization/Schema/SchemaWebSite";

interface OwnProps extends WidgetProps {
    fields: SeoAppProps["fields"];
}

interface SeoGeneratorStyles {
    container?: InjectableCss;
}

const seoGeneratorStyles: SeoGeneratorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = seoGeneratorStyles;
const SeoGenerator: FC<OwnProps> = ({ fields }) => {
    const { isInShell } = useShellContext();
    const allProductsPageUrl = useGetLink(fields.allProductsPage)?.url;

    const props: SeoAppProps = useAppSelector(state => ({
        state,
        currentPage: getCurrentPage(state),
        url: `https://${state?.context?.website?.uri?.split("/")[2].split(":")[0]}`,
        payload: { allProductsPageUrl },
        fields,
    }));

    const build = (extension: SeoHandlerProps) => {
        if (extension?.isActivate(props)) {
            const schema = extension.buildSchema(props);
            if (schema && Object.keys(schema).length > 0) {
                return <JsonLd json={schema} />;
            }
        }
        return null;
    };

    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="SearchEngineOptimization">
                {translate("SearchEngineOptimization Widget")}
            </StyledWrapper>
        );
    }

    return (
        <>
            {build(Organization)}
            {build(Website)}
            {build(WebPage)}
            {build(ContactPage)}
            {build(LocalBusiness)}
            {build(BreadcrumbList)}
            {build(SiteNavigationElement)}
            {build(CollectionPage)}
            {build(Product)}
            {build(Brand)}
        </>
    );
};

const JsonLd: FC<{ json: WithContext<any> }> = ({ json }) =>
    json && Object.keys(json).length > 0 ? (
        <script type="application/ld+json">{JSON.stringify(json, safeJsonLdReplacer)}</script>
    ) : null;

const widgetModule: WidgetModule = {
    component: SeoGenerator,
    definition: {
        group: "Xngage" as any,
        displayName: "Search Engine Optimization",
        allowedContexts: ["Header", "SharedContent"],
        fieldDefinitions: [...seoFieldDefinitions],
    },
};

export default widgetModule;
